import type { NPCModelProps, NPCModelRegistry } from './types';

// ==================== LE (R)ÉVEIL DE L'ÉDUCATRICE NPCs ====================

/**
 * L'Oracle Chevelu - Benours déguisé en sage mystérieux
 *
 * Apparence:
 * - Robe de sage dorée et majestueuse
 * - Chevelure MAGNIFIQUE : longue, ondulée, brillante, qui flotte au vent
 * - Barbe également fournie
 * - Aura mystique
 */
function OracleCheveluModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const robeColor = isSpectral ? '#b8860b' : '#ffd700';
  const robeAccent = isSpectral ? '#8b6914' : '#daa520';
  const hairColor = isSpectral ? '#8b7355' : '#d2691e';
  const skinColor = isSpectral ? '#d8c8b8' : '#ffe4c4';
  const auraColor = isSpectral ? '#9370db' : '#ffd700';

  return (
    <group>
      {/* === BASE / FEET === */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 0.1, 16]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* === ROBE MAJESTUEUSE === */}
      {/* Base de la robe - très large */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <coneGeometry args={[0.35, 0.6, 16]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* Ornements dorés sur la robe */}
      {[0.15, 0.25, 0.35].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.32 - i * 0.05, 0.01, 8, 32]} />
          <meshStandardMaterial color={robeAccent} metalness={0.7} roughness={0.3} />
        </mesh>
      ))}

      {/* Torse */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.25, 12]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* Col brodé */}
      <mesh position={[0, 0.72, 0]} castShadow>
        <torusGeometry args={[0.12, 0.03, 8, 16]} />
        <meshStandardMaterial color={robeAccent} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* === BRAS === */}
      {/* Manches amples */}
      <mesh position={[-0.22, 0.55, 0]} rotation={[0, 0, 0.5]} castShadow>
        <coneGeometry args={[0.1, 0.25, 8]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>
      <mesh position={[0.22, 0.55, 0]} rotation={[0, 0, -0.5]} castShadow>
        <coneGeometry args={[0.1, 0.25, 8]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* Mains */}
      <mesh position={[-0.32, 0.48, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.32, 0.48, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* === TÊTE === */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* === LA MAGNIFIQUE CHEVELURE === */}
      {/* Cheveux principaux - ondulés et volumineux */}
      <mesh position={[0, 0.9, -0.05]} castShadow>
        <sphereGeometry args={[0.14, 16, 16, 0, Math.PI * 2, 0, Math.PI / 1.5]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Mèches latérales qui descendent */}
      <mesh position={[-0.12, 0.75, -0.02]} castShadow>
        <capsuleGeometry args={[0.04, 0.25, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.12, 0.75, -0.02]} castShadow>
        <capsuleGeometry args={[0.04, 0.25, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Cheveux qui flottent vers l'arrière */}
      <mesh position={[-0.08, 0.8, -0.12]} rotation={[0.3, 0, -0.2]} castShadow>
        <capsuleGeometry args={[0.035, 0.2, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.08, 0.8, -0.12]} rotation={[0.3, 0, 0.2]} castShadow>
        <capsuleGeometry args={[0.035, 0.2, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0, 0.78, -0.15]} rotation={[0.4, 0, 0]} castShadow>
        <capsuleGeometry args={[0.04, 0.22, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* === LA MAGNIFIQUE BARBE === */}
      {/* Barbe principale */}
      <mesh position={[0, 0.68, 0.08]} castShadow>
        <coneGeometry args={[0.08, 0.25, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Moustache */}
      <mesh position={[-0.04, 0.78, 0.1]} rotation={[0, 0, 0.3]} castShadow>
        <capsuleGeometry args={[0.015, 0.06, 6, 6]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.04, 0.78, 0.1]} rotation={[0, 0, -0.3]} castShadow>
        <capsuleGeometry args={[0.015, 0.06, 6, 6]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* === YEUX SAGES === */}
      {/* Yeux blancs */}
      <mesh position={[-0.04, 0.87, 0.1]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.04, 0.87, 0.1]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>

      {/* Pupilles (marron sage) */}
      <mesh position={[-0.04, 0.87, 0.12]} castShadow>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      <mesh position={[0.04, 0.87, 0.12]} castShadow>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>

      {/* Sourcils épais */}
      <mesh position={[-0.04, 0.91, 0.1]} rotation={[0, 0, 0.1]} castShadow>
        <boxGeometry args={[0.04, 0.015, 0.02]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.04, 0.91, 0.1]} rotation={[0, 0, -0.1]} castShadow>
        <boxGeometry args={[0.04, 0.015, 0.02]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Nez distingué */}
      <mesh position={[0, 0.83, 0.12]} rotation={[0.3, 0, 0]} castShadow>
        <coneGeometry args={[0.02, 0.05, 6]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* === ACCESSOIRES === */}
      {/* Bâton de sage */}
      <mesh position={[0.4, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.025, 1.0, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>

      {/* Orbe mystique au sommet du bâton */}
      <mesh position={[0.4, 1.05, 0]} castShadow>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshStandardMaterial
          color={auraColor}
          emissive={auraColor}
          emissiveIntensity={0.5}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Médaillon */}
      <mesh position={[0, 0.65, 0.12]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.01, 12]} />
        <meshStandardMaterial color="#ffd700" metalness={0.9} roughness={0.1} />
      </mesh>
      <mesh position={[0, 0.65, 0.125]} castShadow>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial
          color="#ff6b6b"
          emissive="#ff6b6b"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* === AURA MYSTIQUE === */}
      {/* Cercle d'aura au sol */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.3, 0.35, 32]} />
        <meshBasicMaterial color={auraColor} transparent opacity={0.3} />
      </mesh>

      {/* Particules d'aura flottantes */}
      {[0, 1, 2, 3].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i / 4) * Math.PI * 2) * 0.3,
            0.3 + i * 0.15,
            Math.sin((i / 4) * Math.PI * 2) * 0.3,
          ]}
          castShadow
        >
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial
            color={auraColor}
            emissive={auraColor}
            emissiveIntensity={0.5}
            transparent
            opacity={0.7}
          />
        </mesh>
      ))}

      {/* === HOVER EFFECT === */}
      {hovered && (
        <pointLight
          position={[0, 0.8, 0.2]}
          intensity={1}
          color={auraColor}
          distance={3}
        />
      )}
    </group>
  );
}

/**
 * Victorienne Lady - Dame élégante du Hub
 * Style victorien/steampunk
 */
function VictorianLadyModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const dressColor = isSpectral ? '#4a3060' : '#8b008b';
  const skinColor = isSpectral ? '#d8c8b8' : '#ffe4c4';
  const hairColor = isSpectral ? '#3a2a2a' : '#4a3030';

  return (
    <group>
      {/* Jupe large */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <coneGeometry args={[0.3, 0.5, 16]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>

      {/* Torse */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.2, 12]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>

      {/* Tête */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Chignon */}
      <mesh position={[0, 0.82, -0.05]} castShadow>
        <sphereGeometry args={[0.08, 10, 10]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Yeux */}
      <mesh position={[-0.03, 0.77, 0.08]} castShadow>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#4169e1" />
      </mesh>
      <mesh position={[0.03, 0.77, 0.08]} castShadow>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#4169e1" />
      </mesh>

      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#8b008b" distance={2} />
      )}
    </group>
  );
}

/**
 * Gardien du Sanctuaire - Garde mystique
 */
function SanctuaryGuardianModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const armorColor = isSpectral ? '#4a5568' : '#718096';
  const glowColor = isSpectral ? '#805ad5' : '#9f7aea';

  return (
    <group>
      {/* Corps armure */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <boxGeometry args={[0.25, 0.5, 0.15]} />
        <meshStandardMaterial color={armorColor} metalness={0.7} roughness={0.3} />
      </mesh>

      {/* Casque */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={armorColor} metalness={0.7} roughness={0.3} />
      </mesh>

      {/* Visière lumineuse */}
      <mesh position={[0, 0.85, 0.1]} castShadow>
        <boxGeometry args={[0.15, 0.04, 0.03]} />
        <meshStandardMaterial
          color={glowColor}
          emissive={glowColor}
          emissiveIntensity={0.5}
        />
      </mesh>

      {/* Lance */}
      <mesh position={[0.2, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 1.2, 8]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color={glowColor} distance={2} />
      )}
    </group>
  );
}

// ==================== EXPORT REGISTRY ====================

export const reveilEducatriceNPCs: NPCModelRegistry = {
  oracle_chevelu: OracleCheveluModel,
  victorian_lady: VictorianLadyModel,
  sanctuary_guardian: SanctuaryGuardianModel,
};
